import type { CapitalGlobalAddressFieldNames } from '@/features/proposals/components/capital-global/constants/capital-global-proposal-address-field-names.constant';
import { CAPITAL_GLOBAL_PROPOSAL_ADDRESS_FIELD_NAMES } from '@/features/proposals/components/capital-global/constants/capital-global-proposal-address-field-names.constant';
import type { CapitalGlobalProposalStep1FormApi } from '@/features/proposals/components/capital-global/steps/capital-global-proposal-step1-form.types';
import type { CnpjAddress } from '@/services/cnpj/types';

type ApplyOptions = {
  onlyEmpty?: boolean;
};

function shouldApply(
  currentValue: string | undefined,
  nextValue: string,
  onlyEmpty: boolean
): boolean {
  if (!nextValue.trim()) return false;
  if (!onlyEmpty) return true;
  return !String(currentValue || '').trim();
}

/**
 * Aplica o endereço da consulta de CNPJ em QUALQUER bloco de endereço do Step 1 —
 * estipulante (chaves planas) ou subestipulante (`subStipulatorAddresses[i].*`).
 */
export function applyCnpjAddressToAddressFields(
  form: CapitalGlobalProposalStep1FormApi,
  fields: CapitalGlobalAddressFieldNames,
  address: CnpjAddress,
  options: ApplyOptions = {}
): void {
  const onlyEmpty = options.onlyEmpty ?? false;

  const pairs: [string, string][] = [
    [fields.cep, address.zipCode ?? ''],
    [fields.street, address.street ?? ''],
    [fields.number, address.number ?? ''],
    [fields.complement, address.complement ?? ''],
    [fields.neighborhood, address.neighborhood ?? ''],
    [fields.city, address.city ?? ''],
    [fields.state, address.state ?? ''],
  ];

  for (const [name, next] of pairs) {
    const current = form.getFieldValue(name as never) as string | undefined;
    if (!shouldApply(current, next, onlyEmpty)) continue;
    form.setFieldValue(name as never, next as never);
  }
}

export function applyCnpjAddressToRegistrationFields(
  form: CapitalGlobalProposalStep1FormApi,
  address: CnpjAddress,
  options: ApplyOptions = {}
): void {
  applyCnpjAddressToAddressFields(
    form,
    CAPITAL_GLOBAL_PROPOSAL_ADDRESS_FIELD_NAMES.registration,
    address,
    options
  );
}

export function isRegistrationAddressIncomplete(
  values: CapitalGlobalProposalStep1FormApi['state']['values']
): boolean {
  return (
    !String(values.registrationStreet || '').trim() ||
    !String(values.registrationNeighborhood || '').trim() ||
    !String(values.registrationCity || '').trim() ||
    !String(values.registrationState || '').trim()
  );
}

/** CNPJ pode trazer CEP sem logradouro — zipcode completa a rua. */
export function needsZipcodeStreetFallbackAfterCnpj(
  values: CapitalGlobalProposalStep1FormApi['state']['values']
): boolean {
  const cepDigits = String(values.registrationCep || '').replace(/\D/g, '');
  const streetEmpty = !String(values.registrationStreet || '').trim();
  return cepDigits.length === 8 && streetEmpty;
}
